import React, { useState } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@ncoder/ui';
import { GitCommit } from 'lucide-react';
import { CommitCard, CommitData } from '../../atoms/version-control/CommitCard';

interface CommitListProps {
  commits: CommitData[];
  branches: Array<{ name: string; isDefault: boolean }>;
  onViewDetails?: (commitId: string) => void;
  onNewCommit?: (message: string, branch: string) => void;
}

export const CommitList: React.FC<CommitListProps> = ({
  commits,
  branches,
  onViewDetails,
  onNewCommit,
}) => {
  const [commitMessage, setCommitMessage] = useState('');
  const [selectedBranch, setSelectedBranch] = useState(branches.find(b => b.isDefault)?.name || branches[0]?.name || '');
  const [isOpen, setIsOpen] = useState(false);

  const handleCommit = () => {
    if (commitMessage.trim() && onNewCommit) {
      onNewCommit(commitMessage, selectedBranch);
      setCommitMessage('');
      setIsOpen(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-section-title">Recent Commits</h3>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>
              <GitCommit className="h-4 w-4 mr-2" />
              New Commit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Commit</DialogTitle>
              <DialogDescription>
                Commit your current changes to the repository
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="commit-message">Commit Message</Label>
                <Textarea
                  id="commit-message"
                  placeholder="feat: Add new feature or fix: Fix bug"
                  value={commitMessage}
                  onChange={(e) => setCommitMessage(e.target.value)}
                />
              </div>
              <div>
                <Label>Branch</Label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.name} value={branch.name}>
                        {branch.name} {branch.isDefault && '(default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
              <Button onClick={handleCommit}>Commit Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {commits.map((commit) => (
          <CommitCard
            key={commit.id}
            commit={commit}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
    </div>
  );
};
